import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Button,
    Col,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { productActions } from "../../sagas/productSlice";
import PromotionTicketLines from "./PromotionTicketLines";
import { promotionActions } from "../../sagas/promotionSlice";

const getTodayDate = () =>
    new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .split("T")[0];

const buildDefaultLibelle = (values) => {
    if (values.dateDebut && values.dateFin) {
        return `Promotion ${values.dateDebut} au ${values.dateFin}`;
    }

    if (values.dateDebut) {
        return `Promotion ${values.dateDebut}`;
    }

    return "Promotion";
};

const buildDefaultDescription = (values) => {
    const ticketCount = values.tickets?.length || 0;
    const articleLabel = ticketCount > 1 ? "articles" : "article";

    return `Promotion generee automatiquement (${ticketCount} ${articleLabel})`;
};

const createEmptyTicket = (ordreAffichage = 1) => ({
    articleId: "",
    articleCode: "",
    articleLibelle: "",
    prixOriginal: "",
    prixPromo: "",
    barcode: "",
    barcodeType: "CODE128",
    quantiteEtiquettes: 1,
    ordreAffichage,
});

const PromotionFormModal = ({
    isOpen,
    toggle,
    onCreated,
    mode = "promotion",
    title,
    submitLabel,
    initialTickets,
    onSubmit,
    submitting = false,
}) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.promotions);
    const { products, loading: productsLoading } = useSelector((state) => state.products);
    const isPromotionMode = mode === "promotion";

    const initialValues = useMemo(
        () => ({
            libelle: "",
            description: "",
            dateDebut: getTodayDate(),
            dateFin: getTodayDate(),
            tickets:
                initialTickets && initialTickets.length > 0
                    ? initialTickets.map((ticket, index) => ({
                        ...createEmptyTicket(index + 1),
                        ...ticket,
                        ordreAffichage: ticket.ordreAffichage ?? index + 1,
                    }))
                    : [createEmptyTicket(1)],
        }),
        [initialTickets]
    );

    const validationSchema = Yup.object({
        tickets: Yup.array()
            .of(
                Yup.object({
                    articleId: Yup.number().required("Article requis"),
                    articleLibelle: Yup.string().required("Libelle article requis"),
                    prixPromo: isPromotionMode
                        ? Yup.number().required("Prix promo requis")
                        : Yup.mixed().nullable(),
                    barcode: Yup.string().required("Barcode requis"),
                    quantiteEtiquettes: Yup.number().min(1).required("Qte requise"),
                })
            )
            .min(1, "Ajouter au moins une ligne"),
    });

    useEffect(() => {
        if (!isOpen) return;

        dispatch(
            productActions.findAll({
                search: "",
                page: 0,
                size: 100,
            })
        );
    }, [dispatch, isOpen]);

    const productOptions = useMemo(
        () =>
            (products || []).map((product) => ({
                value: product.id,
                label: `${product.code || ""} - ${product.libelle || ""}`,
                searchText: [
                    product.code,
                    product.libelle,
                    product.codeBarre,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase(),
                product,
            })),
        [products]
    );

    const handleProductSearch = (search) => {
        dispatch(
            productActions.findAll({
                search: search || "",
                page: 0,
                size: 100,
            })
        );
    };

    const normalizePayload = (values) => {
        return {
            ...values,
            libelle: values.libelle?.trim() || buildDefaultLibelle(values),
            description:
                values.description?.trim() || buildDefaultDescription(values),
            tickets: values.tickets.map((item) => ({
                ...item,
                articleId: item.articleId ? Number(item.articleId) : null,
                prixOriginal:
                    item.prixOriginal !== "" && item.prixOriginal !== null
                        ? Number(item.prixOriginal)
                        : null,
                prixPromo:
                    item.prixPromo !== "" && item.prixPromo !== null
                        ? Number(item.prixPromo)
                        : null,
                quantiteEtiquettes: item.quantiteEtiquettes
                    ? Number(item.quantiteEtiquettes)
                    : 1,
                ordreAffichage:
                    item.ordreAffichage !== "" && item.ordreAffichage !== null
                        ? Number(item.ordreAffichage)
                        : null,
            })),
        };
    };

    const handleSubmit = (values) => {
        const payload = normalizePayload(values);

        if (!isPromotionMode && onSubmit) {
            onSubmit(payload);
            return;
        }

        dispatch(
            promotionActions.create({
                data: payload,
                onSuccess: (data) => {
                    toggle();
                    if (onCreated) onCreated(data);
                },
                onError: () => {},
            })
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl">
            <ModalHeader toggle={toggle}>
                {title || (isPromotionMode ? "Creer une promotion" : "Generer des etiquettes")}
            </ModalHeader>

            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, handleChange, setFieldValue }) => (
                    <Form>
                        <ModalBody>
                            {isPromotionMode && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Label>Date debut</Label>
                                            <Input
                                                type="date"
                                                name="dateDebut"
                                                value={values.dateDebut}
                                                onChange={handleChange}
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Label>Date fin</Label>
                                            <Input
                                                type="date"
                                                name="dateFin"
                                                value={values.dateFin}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    </Row>

                                    <hr />
                                </>
                            )}

                            <PromotionTicketLines
                                values={values}
                                setFieldValue={setFieldValue}
                                productOptions={productOptions}
                                productsLoading={productsLoading}
                                onProductSearch={handleProductSearch}
                                mode={mode}
                            />
                        </ModalBody>

                        <ModalFooter>
                            <Button color="secondary" onClick={toggle}>
                                Annuler
                            </Button>
                            <Button
                                color="primary"
                                type="submit"
                                disabled={isPromotionMode ? loading : submitting}
                            >
                                {isPromotionMode
                                    ? (loading ? "Enregistrement..." : (submitLabel || "Enregistrer"))
                                    : (submitting ? "Generation..." : (submitLabel || "Generer PDF"))}
                            </Button>
                        </ModalFooter>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default PromotionFormModal;
